// rig space -> world in one quaternion: the solved part fit, then the bundle's
// world_rotation. Both sit above the whole skeleton, so they compose like parent nodes.
// Notes: ~/comments/aos-threejs-poc/rigRenderFrame_rigToWorldRotation_two_transforms.md

import * as THREE from 'three'
import { RIG_UNITS_PER_METRE, matrixDeterminant, permutationMatrix, sharedPermutation } from './partFrameFit.js'

/** Every solved part fit across every branch. */
export function allPartFits(verts) {
  return Object.keys(verts?.branches ?? {}).flatMap((branch) => verts.partFits(branch) ?? [])
}

/** rig space → world, or null when the fits disagree or the fit is a reflection.
 *  @param {object} verts  the PartBranchVertices holding the solved fits
 *  @param {THREE.Quaternion|null} worldRotation  the bundle's world_rotation */
export function rigToWorldRotation(verts, worldRotation = null) {
  const permutation = sharedPermutation(allPartFits(verts))
  if (!permutation) {
    console.warn('[ogs] part fits disagree on a rotation — a generated clip cannot be '
      + 'brought into this rig\'s frame and will play in the frame it was authored in')
    return null
  }
  const matrix = permutationMatrix(permutation)
  if (matrixDeterminant(matrix) < 0) {
    console.warn('[ogs] the part fit is a REFLECTION, not a rotation — leaving generated '
      + 'clips in their authored frame')
    return null
  }
  const fit = new THREE.Quaternion().setFromRotationMatrix(new THREE.Matrix4().set(
    matrix[0], matrix[1], matrix[2], 0,
    matrix[3], matrix[4], matrix[5], 0,
    matrix[6], matrix[7], matrix[8], 0,
    0, 0, 0, 1))
  return worldRotation ? worldRotation.clone().multiply(fit) : fit
}

/** Metres per rig unit: the part fit's cm→m, then the bundle's own world_scale.
 *
 *  The SCALE half of the same two transforms `rigToWorldRotation` composes, and it is
 *  needed for exactly one reason: a generated clip states its root travel in world
 *  METRES, while this skeleton is authored in centimetres and nothing in its own node
 *  hierarchy says so (the chain above `pelvis` is identity here, where the GLB avatar
 *  carries the 0.01 on its Armature). Miss it and a clip's vertical — a jump — lands
 *  100x too small: the character walks, because the travel rides the avatar root in
 *  world metres, and never leaves the ground.
 *  @param {number} worldScale  the bundle's world_scale (1 for a metres bundle) */
export function rigToWorldScale(worldScale = 1) {
  const scale = Number.isFinite(worldScale) && worldScale > 0 ? worldScale : 1
  return scale / RIG_UNITS_PER_METRE
}
